import React from 'react';
import classNames from 'classnames';

import type {
  AttendanceJourneyTimelineItem,
  AttendanceJourneyTimelineItemType,
} from '@src/models/interfaces/attendanceJourneyTimeline.interface';
import IntlMessages from '@src/util/IntlMessages';

import styles from './AttendanceJourneyTimeline.module.less';

const TIMELINE_LABEL_IDS: Record<AttendanceJourneyTimelineItemType, string> = {
  initial: 'markingPortal.marking.timeline.initial',
  breakStart: 'markingPortal.marking.timeline.breakStart',
  breakEnd: 'markingPortal.marking.timeline.breakEnd',
  final: 'markingPortal.marking.timeline.final',
};

export type AttendanceJourneyTimelineProps = {
  items: AttendanceJourneyTimelineItem[];
  orientation?: 'vertical' | 'horizontal';
};

/**
 * Shared journey timeline (initial → break start → break end → final).
 * Used by Marking Portal and Mark Attendance drawer.
 */
const AttendanceJourneyTimeline: React.FC<AttendanceJourneyTimelineProps> = ({
  items,
  orientation = 'vertical',
}) => (
  <div
    role="list"
    className={classNames(styles.timeline, {
      [styles.timelineHorizontal]: orientation === 'horizontal',
    })}
    data-orientation={orientation}
  >
    {items.map((item, index) => {
      const isCompleted = item.status === 'completed';
      const isLast = index === items.length - 1;
      const isConnectorCompleted = items[index + 1]?.status === 'completed';

      return (
        <div key={item.type} role="listitem" className={styles.timelineItem}>
          <div className={styles.timelineTrack}>
            <div
              className={classNames(styles.timelineDot, {
                [styles.timelineDotCompleted]: isCompleted,
              })}
            >
              {isCompleted && (
                <span className="material-symbols-outlined" aria-hidden>
                  check
                </span>
              )}
            </div>
            {!isLast && (
              <div
                className={classNames(styles.timelineLine, {
                  [styles.timelineLineCompleted]: isConnectorCompleted,
                })}
                aria-hidden
              />
            )}
          </div>

          <div className={styles.timelineContent}>
            <span className={styles.timelineLabel}>
              <IntlMessages id={TIMELINE_LABEL_IDS[item.type]} />
            </span>
            <span className={styles.timelineStatus}>
              {isCompleted && item.completedAt ? (
                <IntlMessages
                  id="markingPortal.marking.timeline.markedAt"
                  values={{ time: item.completedAt }}
                />
              ) : item.scheduledAt ? (
                <IntlMessages
                  id="markingPortal.marking.timeline.scheduledAt"
                  values={{ time: item.scheduledAt }}
                />
              ) : (
                item.pendingStatusLabelId && <IntlMessages id={item.pendingStatusLabelId} />
              )}
            </span>
          </div>
        </div>
      );
    })}
  </div>
);

export default React.memo(AttendanceJourneyTimeline);
